import { Controller } from '@hotwired/stimulus'
import { v4 as generateUuid } from 'uuid'
import { loadTemplateEntry } from '@app/adapters/solid/load-template-entry'
import { SfLog } from '@app/adapters/logger'

export default class extends Controller {
  static values = { entry: String, context: Object }

  declare readonly entryValue: string
  declare readonly contextValue: object

  private dispose: (() => void) | null = null
  // Stimulus reuses the controller instance when its element is detached and
  // re-attached, so each connect gets its own token and a mount kicked off by
  // an earlier connect is dropped when it resolves.
  private mountToken: string | null = null

  connect(): void {
    const token = generateUuid()
    this.mountToken = token

    void this.mountEntry(token)
  }

  disconnect(): void {
    this.mountToken = null
    this.dispose?.()
    this.dispose = null
  }

  private async mountEntry(token: string): Promise<void> {
    try {
      const entry = await loadTemplateEntry(this.entryValue)

      // Navigated away (or reconnected) while the module was loading.
      if (this.mountToken !== token) {
        return
      }

      // A Turbolinks cache restore replays the last rendered HTML into the
      // element before connect() fires; clear it so React mounts into a clean
      // node rather than alongside the stale snapshot.
      this.element.replaceChildren()

      this.dispose = entry.mount(this.element as HTMLElement, this.contextValue)
    } catch (error) {
      SfLog.warn('[solid] failed to boot template entry', {
        extra: { entryUrl: this.entryValue, error: String(error) },
      })
    }
  }
}
